import type { ShopperProducts } from '@/scapi';
import { createCategoryUrl, normalizeHierarchyPath, type CategoryWithHierarchyUrl } from './hierarchy-url';

export type CategoryBreadcrumbItem = NonNullable<ShopperProducts.schemas['Category']['parentCategoryTree']>[number];

/**
 * c_hierarchyUrl is often longer than parentCategoryTree — take trailing segments
 * so each crumb gets the right path depth.
 */
export function getBreadcrumbHierarchySegments(
    hierarchyPath: string | undefined | null,
    breadcrumbCount: number
): string[] {
    if (breadcrumbCount <= 0) {
        return [];
    }

    const normalized = normalizeHierarchyPath(hierarchyPath);
    if (!normalized) {
        return [];
    }

    const segments = normalized.split('/').filter(Boolean);
    if (segments.length < breadcrumbCount) {
        return [];
    }

    return segments.slice(segments.length - breadcrumbCount);
}

/** Falls back to flat `/category/:id` when hierarchy data is missing or too short. */
export function createCategoryBreadcrumbUrls(
    items: Array<Pick<CategoryBreadcrumbItem, 'id'>>,
    hierarchyPath: string | undefined | null
): string[] {
    const breadcrumbSegments = getBreadcrumbHierarchySegments(hierarchyPath, items.length);

    return items.map((item, index) => {
        const categoryId = item.id ?? '';

        if (breadcrumbSegments.length === 0) {
            return createCategoryUrl({ id: categoryId });
        }

        return createCategoryUrl({ id: categoryId }, breadcrumbSegments.slice(0, index + 1).join('/'));
    });
}

export function getCategoryBreadcrumbTrail(category: ShopperProducts.schemas['Category']): {
    items: CategoryBreadcrumbItem[];
    hrefs: string[];
} {
    const items: CategoryBreadcrumbItem[] = category.parentCategoryTree ?? [{ id: category.id, name: category.name }];
    const hrefs = createCategoryBreadcrumbUrls(items, (category as CategoryWithHierarchyUrl).c_hierarchyUrl);

    return { items, hrefs };
}
